import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import SignIn from './components/SignIn';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import WatchlistPage from './components/WatchlistPage';
import HighLowPage from './components/HighLowPage';
import RuleStrategyPage from './components/RuleStrategyPage';
import IndexMoverPage from './components/IndexMoverPage';
import NiftyItPage from './components/NiftyItPage';
import NetPositionPage from './components/NetPositionPage';
import DistributionPage from './components/DistributionPage';
import SectorOverview from './components/SectorOverview';
import SectorRotation from './components/SectorRotation';
import IntradayScan from './components/IntradayScan';
import NiftyRealty from './components/NiftyRealty';
import MomentumRadar from './components/MomentumRadar';
import Heatmap from './components/Heatmap';
import OilMonitor from './components/OilMonitor';
import SWPCalculator from './components/SWPCalculator';
import SIPCalculator from './components/SIPCalculator';
import FiiDiiData from './components/FiiDiiData';
import BulkDealsPage from './components/BulkDealsPage';
import ProfilePage from './components/ProfilePage';
import TradersDiary from './components/TradersDiary';
import MainPage from './components/MainPage';
import { WatchlistProvider } from './context/WatchlistContext';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState(null);
  

  useEffect(() => {
  // Check if user is already authenticated
  const userData = localStorage.getItem('user');
  if (userData && userData !== 'undefined') {  // Add check for 'undefined' string
    try {
      const parsedData = JSON.parse(userData);
      if (parsedData && typeof parsedData === 'object') {  // Validate it's an object
        setUser(parsedData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('user');
    }
  }
  setIsLoading(false);
}, []);


  const handleSuccessfulLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));
    setCurrentPage("dashboard");
    // The navigation will be handled by the Router's Navigate component
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentPage('dashboard'); // Reset to dashboard
    window.location.href = '/';
  };

  const renderPage = () => {
    // if admin → show Admin Dashboard only
    if (user?.is_admin) {
      return <AdminDashboard onLogout={handleLogout} />;
    }
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard 
          onLogout={handleLogout}
          onWatchlistClick={() => setCurrentPage('watchlist')}
          onHighLowClick={() => setCurrentPage('highlow')}
          onMomentumClick={() => setCurrentPage('momentum')}
          onRuleStrategyClick={() => setCurrentPage('rule-strategy')}
          onIndexMoverClick={() => setCurrentPage('index-mover')}
          onNetPositionClick={() => setCurrentPage('net-position')}
          onDistributionClick={() => setCurrentPage('distribution')}
          onSectorOverviewClick={() => setCurrentPage('sector-overview')}
          onBulkDealsPageClick={() => setCurrentPage('bulk-deals')}
          onFiiDiiClick={() => setCurrentPage('fii-dii')}
          onNiftyRealtyClick={() => setCurrentPage('nifty-realty')}
          onIntradayScanClick={() => setCurrentPage('intraday-scan')}
          onMomentumRadarClick={() => setCurrentPage('momentum-radar')}
          onHeatmapClick={() => setCurrentPage('heatmap')}
          onOilMonitorClick={() => setCurrentPage('oil-monitor')}
          onSIPCalculatorClick={() => setCurrentPage('sip-calculator')}
          onSWPCalculatorClick={() => setCurrentPage('swp-calculator')}
          onTradersDiaryClick={() => setCurrentPage('traders-diary')}
          onProfileClick={() => setCurrentPage('profile')}
        />;
      case 'watchlist':
        return <WatchlistPage 
          onBack={() => setCurrentPage('dashboard')} 
        />;
      case 'highlow':
        return <HighLowPage 
          onBack={() => setCurrentPage('dashboard')} 
        />;
      
      case 'rule-strategy':
        return <RuleStrategyPage 
          onBack={() => setCurrentPage('dashboard')} 
        />;
      case 'index-mover':
        return <IndexMoverPage 
          onBack={() => setCurrentPage('dashboard')}
          onNiftyItClick={() => setCurrentPage('nifty-it')}
        />;
      case 'nifty-it':
        return <NiftyItPage 
          onBack={() => setCurrentPage('index-mover')} 
        />;
      case 'net-position':
        return <NetPositionPage 
          onBack={() => setCurrentPage('dashboard')} 
        />;
      case 'distribution':
        return <DistributionPage 
          onBack={() => setCurrentPage('dashboard')} 
        />;
      case 'intraday-scan':
        return <IntradayScan onBack={() => setCurrentPage('dashboard')} />;
      case 'momentum-radar':
        return <MomentumRadar onBack={() => setCurrentPage('dashboard')} />;
      case 'heatmap':
        return <Heatmap onBack={() => setCurrentPage('dashboard')} />;
      case 'oil-monitor':
        return <OilMonitor onBack={() => setCurrentPage('dashboard')} />;
      case 'sip-calculator':
        return <SIPCalculator onBack={() => setCurrentPage('dashboard')} />;
      case 'swp-calculator':
        return <SWPCalculator onBack={() => setCurrentPage('dashboard')} />;
      case 'traders-diary':
        return <TradersDiary onBack={() => setCurrentPage('dashboard')} />;
      case 'nifty-realty':
        return <NiftyRealty onBack={() => setCurrentPage('dashboard')} />;
      case 'bulk-deals':
        return <BulkDealsPage onBack={() => setCurrentPage('dashboard')} />;
      case 'fii-dii':
        return <FiiDiiData onBack={() => setCurrentPage('dashboard')} />;
      case 'profile':
        return <ProfilePage onBack={() => setCurrentPage('dashboard')} />;
      case 'sector-overview':
        return <SectorOverview 
          onBack={() => setCurrentPage('dashboard')}
          onSectorSelect={(sector) => {
            console.log('Sector selected:', sector);
            // Directly navigate to the sector rotation page with the selected sector
            setCurrentPage('sector-rotation');
            // Store the selected sector in sessionStorage for SectorRotation to use
            sessionStorage.setItem('selectedSector', sector);
          }}
        />;
      case 'sector-rotation':
        // Get the selected sector from sessionStorage
        const selectedSector = sessionStorage.getItem('selectedSector') || 'Nifty 50';
        console.log('Rendering sector rotation page for:', selectedSector);
        return (
          <SectorRotation 
            key={`sector-rotation-${selectedSector}`}
            initialSector={selectedSector}
            onBack={() => setCurrentPage('sector-overview')} 
          />
        );
        
      default:
        if (currentPage.startsWith('sector-')) {
          const sector = currentPage.replace('sector-', '');
          console.log('Rendering sector page for:', sector);
          return (
            <SectorRotation 
              key={`sector-${sector}`}
              initialSector={sector}
              onBack={() => setCurrentPage('sector-overview')} 
            />
          );
        }
        
        // Default dashboard view
        return (
          <Dashboard 
            onLogout={handleLogout}
            onWatchlistClick={() => setCurrentPage('watchlist')}
            onHighLowClick={() => setCurrentPage('highlow')}
            onMomentumClick={() => setCurrentPage('momentum')}
            onVolumeBoostClick={() => setCurrentPage('volume-boost')}
            onSectorOverviewClick={() => setCurrentPage('sector-overview')}
            onIndexMoverClick={() => setCurrentPage('index-mover')}
            onNetPositionClick={() => setCurrentPage('net-position')}
            onDistributionClick={() => setCurrentPage('distribution')}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <WatchlistProvider>
      <Router>
        <div className="App">
          <Routes>
              <Route path="/" element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <MainPage onSuccessfulLogin={handleSuccessfulLogin} />
                )
              } />
              <Route path="/login" element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <SignIn onLoginSuccess={handleSuccessfulLogin} />
                )
              } />
              <Route path="/dashboard" element={
                isAuthenticated ? (
                  renderPage()
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              {/* Add other protected routes here */}
            </Routes>
        </div>
      </Router>
    </WatchlistProvider>
  );
  
}

export default App;
